/**
 * Visible creative has not changed for longer than its scheduled slot allows.
 * Unlike {@link detectPlaybackTimerStuck}, this ignores whether the loop clock still ticks —
 * a len-1 rotating embed that fails to advance after wrap looks "alive" to the timer detector
 * while the same frame stays painted (#1382 / #1384).
 */

/** Absolute floor before content stall is suspected (ms). */
export const CONTENT_NOT_ADVANCING_MIN_STALL_MS = 5_000

/** Overrun past scheduled duration before suspecting (ms). */
export const CONTENT_NOT_ADVANCING_SLOT_OVERRUN_MARGIN_MS = 1_500

/** Multiplier on scheduled duration for the stall budget. */
export const CONTENT_NOT_ADVANCING_DURATION_MULTIPLIER = 3

export interface IContentNotAdvancingDetectInput {
  documentVisible: boolean
  queueLength: number
  surfaceFrameCount: number
  visibleFrameId: string | null
  /** Wall ms when the painted frame identity last changed. */
  visibleFrameChangedAtMs: number
  /** Scheduled on-air duration (ms); 0 / unknown → never suspect (avoids long-video floor FPs). */
  frameDurationMs: number
  nowMs: number
  /** Wall ms since the last playlist loop wrap; null when none observed yet. */
  msSinceLastLoopWrap: number | null
  /**
   * Whether the last loop wrap actually produced different content. A pool with a single eligible
   * creative keeps wrapping and reporting `false` — that screen is static by configuration, not frozen.
   */
  lastLoopWrapAdvancedContent: boolean
  suppressBecauseRegistrationResync: boolean
  suppressBecauseRegistrationTransitionHold: boolean
  weeklyAvailabilitySuppressed: boolean
}

export interface IContentNotAdvancingStatus {
  suspected: boolean
  /** How long the same visible frame id has been painted (ms). */
  dwellMs: number
  /** Stall budget that was exceeded (ms). */
  thresholdMs: number
}

export function contentNotAdvancingThresholdMs(frameDurationMs: number): number {
  if (!(frameDurationMs > 0)) {
    return CONTENT_NOT_ADVANCING_MIN_STALL_MS
  }
  return Math.max(
    CONTENT_NOT_ADVANCING_MIN_STALL_MS,
    frameDurationMs * CONTENT_NOT_ADVANCING_DURATION_MULTIPLIER + CONTENT_NOT_ADVANCING_SLOT_OVERRUN_MARGIN_MS
  )
}

export function detectContentNotAdvancing(
  input: IContentNotAdvancingDetectInput
): IContentNotAdvancingStatus {
  const thresholdMs = contentNotAdvancingThresholdMs(input.frameDurationMs)
  if (
    !input.documentVisible
    || input.weeklyAvailabilitySuppressed
    || input.queueLength < 1
    || input.surfaceFrameCount < 1
    || input.visibleFrameId == null
    || input.visibleFrameId === ''
    || input.visibleFrameChangedAtMs <= 0
    || input.suppressBecauseRegistrationResync
    || input.suppressBecauseRegistrationTransitionHold
  ) {
    return { suspected: false, dwellMs: 0, thresholdMs }
  }
  const dwellMs = Math.max(0, input.nowMs - input.visibleFrameChangedAtMs)
  if (dwellMs < thresholdMs) {
    return { suspected: false, dwellMs, thresholdMs }
  }
  /**
   * Unknown scheduled duration: do not fault. A long video whose duration has not been
   * materialized yet would otherwise trip the 5s floor and spam recovery (#1384 review).
   * Len-1 freeze detection still works once the slot duration is known.
   */
  if (!(input.frameDurationMs > 0)) {
    return { suspected: false, dwellMs, thresholdMs }
  }
  /**
   * Multi-frame plans (#1500): a painted id that outlives ~3× its slot means the schedule wanted to
   * leave that creative. Do **not** clear on "wraps arrive but embeds did not advance" — that path is
   * only valid for len-1 / single-eligible-pool screens. Applying it on multi-frame let recovery’s
   * periodic wrap stamps clear Admin `content_not_advancing` while the same frame stayed painted.
   */
  if (input.queueLength >= 2) {
    return { suspected: true, dwellMs, thresholdMs }
  }

  /**
   * Len-1 / rotating embed: a long dwell alone is not a fault when the pool holds one eligible
   * creative (wraps keep arriving with `lastLoopWrapAdvancedContent === false`). Only fault when
   * the plan wanted to move — wrap swapped in different content the surface never took, or wraps
   * stopped (including never observed: `msSinceLastLoopWrap == null`) — the freeze itself (#1382).
   *
   * With a known slot duration the stall budget is ≥ ~3× the slot, so a healthy first wrap at ~1× slot
   * arrives before this path can fire. `null` must stay "wraps stopped" so a freeze that never fires
   * the loop listener is still detected.
   */
  const wrapsStopped = input.msSinceLastLoopWrap == null || input.msSinceLastLoopWrap >= thresholdMs
  if (!wrapsStopped && !input.lastLoopWrapAdvancedContent) {
    return { suspected: false, dwellMs, thresholdMs }
  }
  return { suspected: true, dwellMs, thresholdMs }
}
